import { useMemo, useState } from "react";
import { ROUTES, SELF_ONBOARDING_ROUTES } from "src/consts/routes";
import { useNavigate } from "react-router-dom";
import Modal from "src/components/Modal/Modal";
import { Trans, useTranslation } from "react-i18next";
import Text from "src/components/Displays/Text";
import { useToggle } from "src/hooks/useToggle";
import { Stack, useTheme, Box, Divider } from "@mui/material";
import { Tariff } from "src/models/clientsModel";
import PageTitle from "src/components/Displays/PageTitle";
import FaqOverview from "./components/FaqOverview";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import PrimaryButton from "src/components/Shared/PrimaryButton";
import {
  BONUS,
  SESSION_KEYS,
  UI_FLOW_PREFERENCES,
  USER_PROFILE_DATA,
} from "src/consts/session";
import WhatsAppBox from "../WhatsAppBox";
import TarifSelectionContainer from "src/pages/Tarif/components/TarifSelectionContainer";
import LogoSMA from "src/assets/icons/logo_sma.svg";
import {
  LichtBlickLogoSMA,
  Logo,
  LichtBlickLogo,
  TestemonialPortrait,
} from "src/assets";
import { ThemeValues } from "../../components/ThemeUsage/theme";
import { ThemeContext } from "../../components/ThemeUsage/ThemeUsage";
import { useContext } from "react";
import { dateFormat } from "src/helpers/dateFormat";
import CertificateSection from "src/components/Common/CertificateSection";

const TarifSelection = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const themeName = useContext(ThemeContext);

  const [showTariffTimeline] = useState(true);
  const [isFaqOverlayOpen, toggleFaqIsOverlayOpen] = useToggle(false);

  const [getBonus] = useSessionStorage<BONUS | null>(SESSION_KEYS.BONUS);
  const showBonusCampaignFooter = getBonus()?.isActive ?? false;

  const [getSessionStorageItem] = useSessionStorage<Tariff[]>(
    SESSION_KEYS.TARIFFS,
  );
  const [userProfileData] = useSessionStorage<USER_PROFILE_DATA>(
    SESSION_KEYS.USER_PROFILE_DATA,
  );
  const [getUiFlowPreferences] = useSessionStorage<UI_FLOW_PREFERENCES>(
    SESSION_KEYS.UI_FLOW_PREFERENCES,
  );

  const { tariffsData } = useMemo(() => {
    return { tariffsData: getSessionStorageItem() };
  }, [getSessionStorageItem]);

  const savingsData = tariffsData && tariffsData[0];
  const monthlyCosts = savingsData?.fixTariff?.monthlyCosts ?? "";
  const yearlyCosts = savingsData?.fixTariff?.yearlyCosts ?? "";
  const yearlyGridSupplyKwh = userProfileData()?.yearlyGridSupplyKwh ?? "";
  const bonusEndDate = getBonus()?.endDate ?? "";

  const footnote1Text = t("page.tariff.footnotes.footnote1");
  const footnote2Text = t("page.tariff.footnotes.footnote2", {
    yearlyGridSupplyKwh,
    monthlyCosts,
    yearlyCosts,
  });
  const footnote4Text = t("page.tariff.footnotes.footnote4", {
    bonusEndDate: dateFormat(bonusEndDate),
  });
  const footnoteLastText = t("page.tariff.footnotes.last");

  const [getTransferId] = useSessionStorage<string>(SESSION_KEYS.TRANSFER_ID);
  const [getSalesPartnerId] = useSessionStorage<string>(
    SESSION_KEYS.SALES_PARTNER_ID,
  );

  const transferId = getTransferId();
  const salesPartnerId = getSalesPartnerId();

  const isCustomTheme = themeName === "sma" || themeName === "disco";

  const getLogo = () => {
    switch (themeName) {
      case ThemeValues.SMA:
      case ThemeValues.lichtblick:
        return <LichtBlickLogoSMA />;
      case ThemeValues.default:
      default:
        return <Logo color="white" />;
    }
  };

  const horizontalPadding =
    themeName === ThemeValues.SMA ? { xs: 6, sm: 10.5 } : { xs: 4, sm: 9 };

  if (!tariffsData || tariffsData.length === 0) {
    return (
      <PrimaryButton
        text={t("page.introduction.textNewCalculation")}
        onClick={() => navigate(ROUTES.INTRODUCTION)}
        sx={{
          textTransform: "none",
          background: isCustomTheme ? undefined : "white",
          color: isCustomTheme ? undefined : "black !important",
          "&:hover": {
            background: isCustomTheme ? undefined : "white",
            color: isCustomTheme ? undefined : "black",
          },
        }}
      />
    );
  }

  const navigateTo = () => {
    const uiFlowPreferences = getUiFlowPreferences();
    if (
      uiFlowPreferences &&
      uiFlowPreferences.skipOemAuth &&
      uiFlowPreferences.skipOemAuth === "false" &&
      uiFlowPreferences.processType === "SITE_FULL_SELF_V1"
    ) {
      return SELF_ONBOARDING_ROUTES.ACCESS;
    }
    return ROUTES.YOUR_NAME;
  };

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        mt={2}
        px={horizontalPadding}
        mx={{ xs: -3, sm: -7.5 }}
        spacing={1}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            height: { xs: 30, sm: 50 },
            width: "auto",
          }}
        >
          {themeName === ThemeValues.SMA && (
            <Box
              component="img"
              src={LogoSMA}
              alt="Logo SMA"
              sx={{ height: "100%", width: "auto" }}
            />
          )}
          {themeName === ThemeValues.ison && <LichtBlickLogo color="white" />}
        </Stack>

        <Box
          sx={{
            height: { xs: 30, sm: 50 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {getLogo()}
        </Box>
      </Stack>

      <Stack gap={12}>
        <Box
          sx={{
            width: "100%",
            minWidth: { xs: "100%", lg: "850px" },
          }}
        >
          <TarifSelectionContainer
            showTrustBadges={false}
            showSavingsResult={true}
            tariffsData={tariffsData}
            showTariffTimeline={showTariffTimeline}
            topTextKey="page.tariff.tariffSelectionContainer.howTariffChangeWorks"
          />
          <PrimaryButton
            sx={{
              width: { xs: "100%", md: "410px" },
              ml: { xs: "0px", md: "11px" },
            }}
            text={t("page.calculatedSavingsSummary.choseContract")}
            onClick={() => navigate(navigateTo())}
          />
        </Box>
        <Stack gap={5} sx={{ ml: { xs: "0px", md: "11px" } }}>
          <Stack
            direction={{ xs: "column", lg: "row" }}
            gap={3}
            sx={{
              width: "100%",
              alignItems: "flex-start",
            }}
          >
            <Stack
              sx={{
                width: "100%",
                minWidth: { xs: "100%", md: "410px" },
                flex: "1 1 auto",
              }}
              gap={3}
              alignItems="center"
            >
              <Box
                sx={{
                  width: "100%",
                  bgcolor: "#FFFFFF",
                  borderRadius: "16px",
                  p: { xs: 2.5, sm: 3 },
                  boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.04)",
                  border: "1px solid #E5E7EB",
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  boxSizing: "border-box",
                }}
              >
                <Stack direction="row" spacing={6} alignItems="center">
                  <Box
                    component="img"
                    src={TestemonialPortrait}
                    alt="Max M."
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      objectFit: "cover",
                      flexShrink: 0,
                    }}
                  />
                  <Stack gap={1}>
                    <Text
                      text="„Endlich spare ich mit meiner PV-Anlage und meinem E-Auto noch mehr Stromkosten, automatisch.“"
                      sx={{
                        fontSize: "14px",
                        fontWeight: "400",
                        lineHeight: "20px",
                        color: "#333333",
                      }}
                    />
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      gap={1}
                    >
                      <Text
                        text="Max M., Hamburg"
                        sx={{
                          fontSize: "12px",
                          color: "#31825E",
                          fontWeight: "600",
                        }}
                      />
                      <Box
                        sx={{
                          bgcolor: "#C6FFB2",
                          color: "#31825E",
                          fontSize: "11px",
                          fontWeight: "700",
                          px: 1,
                          py: 0.25,
                          borderRadius: "12px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        spart 340 € / Jahr
                      </Box>
                    </Stack>
                  </Stack>
                </Stack>

                <Divider />

                <Stack gap={1.5} alignItems="center">
                  <CertificateSection smallerBadges={true} />
                </Stack>
              </Box>
            </Stack>

            <Box
              sx={{
                flex: 1,
                width: "100%",
                minWidth: { xs: "100%", sm: "420px" },
                mx: "auto",
              }}
            >
              <WhatsAppBox />
            </Box>
          </Stack>
          {/*
          <Stack
            sx={{ cursor: "pointer" }}
            direction="row"
            gap={1}
            justifyContent="center"
            onClick={() => {
              setBonus(null);
              setTariffs([]);
              navigate(ROUTES.PV_SETUP);
            }}
          >
            <ArrowLeft color={theme.palette.mainFont.primary} />
            <Text
              sx={(theme) => ({
                color: theme.palette.mainFont.primary,
              })}
              text={t("page.calculatedSavings.newPriceCalculation")}
            />
          </Stack>
          */}
          <Stack
            sx={{ cursor: "pointer" }}
            direction="row"
            gap={1}
            justifyContent={{ xs: "center", sm: "left" }}
            onClick={() =>
              navigate(
                ROUTES.INTRODUCTION +
                  `?tid=${transferId}&spid=${salesPartnerId}&theme=${themeName}&openSummary=true`,
              )
            }
          >
            <Text
              sx={(theme) => ({
                color: theme.palette.mainFont.primary,
              })}
              text={t("page.tariff.details")}
            />
          </Stack>

          <Stack
            gap={1}
            direction="row"
            justifyContent={{ xs: "center", sm: "left" }}
            sx={{ cursor: "pointer" }}
            onClick={toggleFaqIsOverlayOpen}
          >
            <Text
              sx={(theme) => ({
                color: theme.palette.mainFont.primary,
              })}
              text={t("page.tariff.faq")}
            />
          </Stack>
        </Stack>
        <Stack gap={4}>
          <Text
            sx={{
              color: "#848F9F",
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "18px",
            }}
            text={footnote1Text}
          />
          <Text
            sx={{
              color: "#848F9F",
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "18px",
            }}
            text={footnote2Text}
          />

          <Box
            sx={{
              color: "#848F9F",
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "18px",
              "& a": {
                color: "the",
                textDecoration: "none",
              },
              "& a:hover": {
                textDecoration: "underline",
              },
            }}
          >
            <Trans
              i18nKey="page.tariff.footnotes.footnote3"
              components={{
                1: (
                  <a
                    href="https://www.lichtblick.de/oekostrom/stromkennzeichnung/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="View electricity labeling"
                  />
                ),
              }}
            />
          </Box>

          {showBonusCampaignFooter && (
            <Text
              sx={{
                color: "#848F9F",
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "18px",
              }}
              text={footnote4Text}
            />
          )}
          <Text
            sx={{
              color: "#848F9F",
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "18px",
            }}
            text={footnoteLastText}
          />
        </Stack>
      </Stack>

      <Modal
        title={
          <PageTitle
            color={theme.palette.grey[500]}
            text={t("page.tariff.faqModal.title")}
          />
        }
        isOpen={isFaqOverlayOpen}
        onClose={toggleFaqIsOverlayOpen}
        onSubmit={toggleFaqIsOverlayOpen}
      >
        <FaqOverview />
      </Modal>
    </>
  );
};

export default TarifSelection;
